<template>
  <section class="form-creator">
    <!--
      Header
    -->
    <div class="header add--text-align-center">
      <input
        class="header__primary"
        v-if="
          editingLanguage == $settingsSecondLanguageType && !$isSingleLanguage
        "
        :placeholder="`Click to add title... (${$settingsSecondLanguageType})`"
        v-model="existingForm.titleSec"
        :maxlength="28"
        required
      />
      <input
        class="header__primary"
        v-else
        placeholder="Click to add title..."
        v-model="existingForm.title"
        :maxlength="28"
        required
      />
      <div class="header__secondary-wrapper">
        <textarea
          class="header__secondary"
          v-if="
            editingLanguage == $settingsSecondLanguageType && !$isSingleLanguage
          "
          :placeholder="`Click to add subtitle... (${$settingsSecondLanguageType})`"
          v-model="existingForm.subtitleSec"
          :maxlength="65"
          wrap="soft"
        />
        <textarea
          class="header__secondary"
          v-else
          placeholder="Click to add subtitle..."
          v-model="existingForm.subtitle"
          :maxlength="65"
          wrap="soft"
        />
      </div>
    </div>

    <div class="form-category-list">
      <draggable
        v-model="form.categories"
        @start="drag = true"
        @end="drag = false"
      >
        <div
          class="form-category"
          v-for="(category, categoryIndex) in existingForm.categories"
          :key="categoryIndex"
        >
          <div
            class="form-category__header"
            :style="{ backgroundColor: existingForm.formColor }"
          >
            <div
              class="form-category-remove"
              @click="removeCategory(categoryIndex)"
            >
              <v-icon name="times-circle" style="align-self: center"></v-icon>
            </div>
            <input
              class="form-category__title"
              v-if="
                editingLanguage == $settingsSecondLanguageType &&
                !$isSingleLanguage
              "
              :placeholder="`Add title to category... (${$settingsSecondLanguageType})`"
              v-model="category.titleSec"
              :style="{ color: existingForm.textColor }"
              :maxlength="28"
            />
            <input
              class="form-category__title"
              v-else
              placeholder="Add title to category..."
              v-model="category.title"
              :style="{ color: existingForm.textColor }"
              :maxlength="categoryMaxLength"
            />
          </div>

          <ul class="form-items-list">
            <draggable
              v-model="category.items"
              @start="drag = true"
              @end="drag = false"
            >
              <li
                class="form-item"
                v-for="(item, itemIndex) in category.items"
                :key="itemIndex"
              >
                <div
                  class="form-item-title"
                  v-if="
                    editingLanguage == $settingsSecondLanguageType &&
                    !$isSingleLanguage
                  "
                >
                  {{ item.titleSec }}
                </div>
                <div class="form-item-title" v-else>{{ item.title }}</div>
                <div class="form-item-price">
                  {{ item.price }} {{ existingForm.currency }}
                </div>
                <div
                  class="form-item-remove"
                  @click="removeItem(categoryIndex, itemIndex)"
                >
                  <v-icon
                    name="times-circle"
                    color="#707070"
                    style="align-self: center"
                  ></v-icon>
                </div>
                <div
                  class="form-item-edit"
                  @click="editItem(categoryIndex, itemIndex, item)"
                >
                  <v-icon
                    name="pencil-alt"
                    color="#707070"
                    style="align-self: center"
                  ></v-icon>
                </div>
              </li>
            </draggable>

            <div
              class="add-item__new-button"
              v-if="currentCategoryIndex != categoryIndex"
              @click="resetInputs(categoryIndex)"
            >
              NEW ITEM
              <v-icon
                class="form-category-add__icon"
                name="plus-circle"
                scale="1.3"
                color="#48B9BA"
              ></v-icon>
            </div>
            <div class="add-item" v-if="currentCategoryIndex == categoryIndex">
              <div class="row">
                <input
                  class="add-item__title"
                  :style="{
                    border: isTitleEmpty
                      ? '2px solid #D95D5D'
                      : '1px solid #AAAAAA',
                  }"
                  :disabled="editingItem"
                  @input="
                    (e) => {
                      itemTitle = e.target.value;
                      currentCategoryIndex = categoryIndex;
                    }
                  "
                  :value="
                    currentCategoryIndex == categoryIndex ? itemTitle : ''
                  "
                  :placeholder="placeholderPrimary('title')"
                  :maxlength="itemMaxLength"
                />
              </div>
              <div class="row" v-if="!$isSingleLanguage">
                <input
                  class="add-item__title"
                  :style="{
                    border:
                      isSecondaryTitleEmpty
                        ? '2px solid #D95D5D'
                        : '1px solid #AAAAAA',
                  }"
                  :disabled="editingItem"
                  @input="
                    (e) => {
                      itemTitleSec = e.target.value;
                      currentCategoryIndex = categoryIndex;
                    }
                  "
                  :value="
                    currentCategoryIndex == categoryIndex ? itemTitleSec : ''
                  "
                  :placeholder="placeholderSecondary('title')"
                  :maxlength="itemMaxLength"
                />
              </div>
              <div class="row">
                <input
                  class="add-item__description"
                  @input="
                    (e) => {
                      itemDescription = e.target.value;
                      currentCategoryIndex = categoryIndex;
                    }
                  "
                  :value="
                    currentCategoryIndex == categoryIndex ? itemDescription : ''
                  "
                  :placeholder="placeholderPrimary('description')"
                  :maxlength="itemDescriptionMaxLength"
                />
                <input
                  class="add-item__price"
                  :style="{
                    border: isPriceEmpty
                      ? '2px solid #D95D5D'
                      : '1px solid #AAAAAA',
                  }"
                  :disabled="editingItem"
                  @input="
                    (e) => {
                      itemPrice = e.target.value;
                      currentCategoryIndex = categoryIndex;
                    }
                  "
                  :value="
                    currentCategoryIndex == categoryIndex ? itemPrice : ''
                  "
                  placeholder="Insert price"
                  onkeypress="return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? null : event.charCode >= 48 && event.charCode <= 57"
                  :maxlength="priceMaxLength"
                />
              </div>
              <div class="row">
                <input
                  class="add-item__description"
                  v-if="!$isSingleLanguage"
                  @input="
                    (e) => {
                      itemDescriptionSec = e.target.value;
                      currentCategoryIndex = categoryIndex;
                    }
                  "
                  :value="
                    currentCategoryIndex == categoryIndex ? itemDescriptionSec : ''
                  "
                  :placeholder="placeholderSecondary('description')"
                  :maxlength="itemDescriptionMaxLength"
                />
                <div
                  class="add-item__add-button"
                  :style="{ color: '#FFFFFF', backgroundColor: '#48b9ba' }"
                  @click="addItem(editingItem, itemTitle, itemTitleSec, itemDescription, itemDescriptionSec, itemPrice)"
                >
                  SAVE ITEM
                </div>
              </div>
            </div>
          </ul>
        </div>
      </draggable>
      <!-- <div class="form-category-add" @click="addCategory" :style="{backgroundColor: form.formColor}"> -->
      <div
        class="form-category-add"
        @click="addCategory"
        :style="{ backgroundColor: '#48b9ba' }"
      >
        <p class="form-category-add__text" :style="{ color: '#FFFFFF' }">
          ADD CATEGORY
        </p>
        <!-- <p class="form-category-add__text" :style="{color: form.textColor}" >Add category</p> -->

        <v-icon
          class="form-category-add__icon"
          name="plus-circle"
          scale="1.3"
        ></v-icon>
      </div>
    </div>
  </section>
</template>

<script>
import generateUuidMixin from "@/mixins/generateUuidMixin";
import draggable from "vuedraggable";

export default {
  components: {
    draggable,
  },
  props: {
    existingForm: [Array, Object],
    editingLanguage: String,
  },
  data() {
    return {
      form: {},
      itemTitle: "",
      itemTitleSec: "",
      itemDescription: "",
      itemDescriptionSec: "",
      itemPrice: "",
      currentCategoryIndex: -1,
      editableItemIndex: -1,
      itemMaxLength: 30,
      itemDescriptionMaxLength: 100,
      categoryMaxLength: 28,
      priceMaxLength: 6,
      isTitleEmpty: false,
      isPriceEmpty: false,
      isSecondaryTitleEmpty: false,
      editingItem: false,
    };
  },
  created() {
    if (this.existingForm) {
      this.form = this.existingForm;
    }
    if (
      this.form.categories[0].title == "" &&
      this.form.categories[0].titleSec == ""
    ) {
      this.form.categories = [];
    }
  },
  watch: {
    form: {
      handler: function (newValue) {
        this.$emit("updateForm", newValue);
      },
      deep: true,
    },
  },
  mounted: function () {
    document.addEventListener("keydown", this.handleKeydown);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  mixins: [generateUuidMixin],
  methods: {
    addCategory() {
      if (this.form.categories) {
        this.form.categories.push({
          items: [],
          title: "",
          titleSec: "",
        });
      }
    },
    handleKeydown(e) {
      if (
        e.keyCode == 13 &&
        this.itemTitle.length > 0 &&
        this.itemPrice.length > 0
      ) {
        this.addItem(this.editingItem, this.itemTitle, this.itemTitleSec, this.itemDescription, this.itemDescriptionSec, this.itemPrice);
      }
    },
    addItem(editingItem, title, titleSec, description, descriptionSec, price) {
      
      if(editingItem) {
        this.form.categories[this.currentCategoryIndex].items[this.editableItemIndex].descriptions = [
          description,
          this.$isSingleLanguage ? description : descriptionSec
        ]
        this.editableItemIndex = -1;
        this.currentCategoryIndex = -1;
        this.editingItem = false;
        return;
      }

      if (!this.$isSingleLanguage) {
        if (title.length > 0 && titleSec.length > 0 && Number(price) > 0) {
          this.form.categories[this.currentCategoryIndex].items.push({
            title: title,
            titleSec: titleSec,
            descriptions: [
              description,
              descriptionSec
            ],
            price: Number(price),
            amount: 0,
            id: this.generateUUID(),
          });
        } else {
          this.isTitleEmpty = title.length == 0 ? true : false;
          this.isPriceEmpty = price.length == 0 ? true : false;
          this.isSecondaryTitleEmpty = titleSec.length == 0 ? true : false;
          return;
        }
        this.isTitleEmpty = false;
        this.isPriceEmpty = false;
        this.isSecondaryTitleEmpty = false;
      } else {
        if (title.length > 0 && Number(price) > 0) {
          this.form.categories[this.currentCategoryIndex].items.push({
            title: title,
            titleSec: title,
            descriptions: [
              description,
              description
            ],
            price: Number(price),
            amount: 0,
            id: this.generateUUID(),
          });
        } else {
          this.isTitleEmpty = title.length == 0 ? true : false;
          this.isPriceEmpty = price.length == 0 ? true : false;
          return;
        }
      }
      this.currentCategoryIndex = -1;
    },
    editItem(categoryIndex, itemIndex, item) {
      this.currentCategoryIndex = categoryIndex;
      this.editableItemIndex = itemIndex;
      this.itemTitle = item.title;
      this.itemTitleSec = item.titleSec;
      this.itemDescription = item.descriptions ? item.descriptions[0] : '';
      this.itemDescriptionSec = item.descriptions ? item.descriptions[1] : '';
      this.itemPrice = item.price;
      this.editingItem = true;
    },
    placeholderPrimary(type) {
      let placeholderText = type === 'title' ? "Insert item name" : "OPTIONAL: Insert item description";
      if (!this.$isSingleLanguage) {
        placeholderText += ` (${this.$settingsDefaultLanguageType})`;
      }
      return placeholderText;
    },
    placeholderSecondary(type) {
      let placeholderText = type === 'title' ? "Insert item name" : "OPTIONAL: Insert item description";
      if (!this.$isSingleLanguage) {
        placeholderText += ` (${this.$settingsSecondLanguageType})`;
      } else {
        placeholderText = "Only one language enabled";
      }
      return placeholderText;
    },
    resetInputs(categoryIndex) {
      this.currentCategoryIndex = categoryIndex;
      this.isTitleEmpty = false;
      this.isPriceEmpty = false;
      this.isSecondaryTitleEmpty = false;
      this.itemTitle = '';
      this.itemTitleSec = '';
      this.itemDescription = '';
      this.itemDescriptionSec = '';
      this.itemPrice = '';
    },
    removeCategory(categoryIndex) {
      this.form.categories.splice(categoryIndex, 1);
    },
    removeItem(categoryIndex, itemIndex) {
      this.form.categories[categoryIndex].items.splice(itemIndex, 1);
    },
    charactersRemaining(string) {
      return string ? string.length : 0;
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0px;
  padding: 0px;
}

/*
    Header
  */

.header {
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
}

.header__primary {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  font-size: 30px !important;
}

.header__secondary-wrapper {
  width: 100%;
  height: 36px;
}

.header__secondary {
  font-size: 18px !important;
  width: 80%;
  text-align: center;

  border: none;
  overflow: hidden;
  outline: none;

  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;

  resize: none;
}

.header input {
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
}

.form-category__header input {
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
}

/*
    Add item
  */

.add-item {
  font-size: 12px;
  cursor: initial;
}

.row {
  display: flex;
  justify-content: center;
  align-items: center;
}

.add-item__title {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #aaaaaa;
  box-sizing: border-box;
  border-radius: 8px;
  background: #ffffff;
  outline: none;
  margin: 7px 0px 0px;
  height: 40px;
  width: 100%;
}

.add-item__price {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #aaaaaa;
  box-sizing: border-box;
  border-radius: 8px;
  background: #ffffff;
  outline: none;
  margin: 7px 0px 0px;
  width: 20%;
  height: 40px;
}

.add-item__description {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #aaaaaa;
  box-sizing: border-box;
  border-radius: 8px;
  background: #ffffff;
  outline: none;
  margin: 7px 0px 0px;
  height: 40px;
  width: 80%;
  margin-right: 9px;
}

.add-item__price::placeholder,
.add-item__title::placeholder {
  text-align: center;
}

.add-item__add-button {
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  border-radius: 8px;
  background: #ffffff;
  outline: none;
  margin: 7px 0px 0px;
  width: 20%;
  cursor: pointer;
  height: 40px;
}

.add-item__new-button {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  background: #ffffff;
  border: 1px solid #aaaaaa;
  border-top: 0px;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 0px 0px 8px 8px;
}

/* To prevent vertical scrollings of the inputs - TODO fix properly */
input {
  line-height: 100%;
}

.form-creator {
  padding: 5px 50px;
  height: 100%;
  overflow: auto;
  border: 2px solid #aaaaaa;
  border-radius: 8px;
  background-color: #ffffff;
}

.form-add-item {
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(112, 112, 112, 0.5);
}

.form-creator input:focus::placeholder {
  color: transparent;
}

.form-category {
  display: flex;
  width: 100%;
  flex-direction: column;
  border-radius: 8px;
  margin-bottom: 15px;
  cursor: move;
}

.form-category__header,
.form-category-add {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  color: white;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  height: 40px;
  font-weight: 500;
}

.form-category-add {
  cursor: pointer;
  border-radius: 8px;
  margin: 10px 0px;
}

.form-category__title,
.form-category__title::placeholder {
  color: white;
  width: 80%;
}

.form-items-list {
  text-decoration: none;
  display: flex;
  flex-direction: column;
}

.form-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 40px;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  cursor: move;
  border: 1px solid rgba(112, 112, 112, 0.5);
  border-top: 0px solid rgba(112, 112, 112, 0.5);
}

.form-item-add {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.form-category-remove,
.form-item-remove,
.form-item-edit {
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 40px;
  height: 20px;
  position: absolute;
  left: 0;
}
.form-item-edit {
  left: 30px;
}

.form-item-add__icon,
.form-category-add__icon {
  margin-left: 5px;
}

.form-item-title {
  padding-right: 5px;
  padding-left: 70px;
  width: 80%;
}

.item__title-input-secondary {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 201.5px;
  border-left: 1px solid rgba(112, 112, 112, 0.5);
}

.item__title-input-secondary::placeholder,
.form-item-price::placeholder {
  color: rgba(255, 255, 255, 0.705);
  border-left: 1px solid rgba(112, 112, 112, 0.5);
}

.item__title-input::placeholder {
  color: rgba(255, 255, 255, 0.705);
  border-left: none;
}

.form-item-price {
  font-weight: 500;
  line-height: 24px;
  font-weight: bold;
  border-left: 1px solid rgba(112, 112, 112, 0.5);
  width: 20%;
  text-align: center;
}

</style>
